import { Component, OnInit } from '@angular/core';
import { take, catchError } from 'rxjs/operators';
import moment from 'moment';
import { Product } from 'src/app/order/models/product.model';
import { ProductService } from 'src/app/quoting/services/product.service';
import { Warehouse } from '../../models/warehouse.model';
import { WarehouseLocation } from '../../models/warehouse-location.model';
import { ProductStockMovementReference } from '../../models/product-stock-movement-reference.model';
import { MovementRow, AdjustmentTotals, AdjustmentByReference, AdjustmentsResult } from '../../models/warehouse-reporting.model';
import { WarehouseService } from '../../services/warehouse.service';
import { WarehouseLocationService } from '../../services/warehouse-location.service';
import { StockMovementReferenceService } from '../../services/stock-movement-reference.service';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { StockWeightService } from '../../services/stock-weight.service';
import { ReportCsvService } from '../../services/report-csv.service';
import { WarehousingReportFilters } from '../../services/warehousing-report.service';
import { WarehouseReportingService } from '../../services/warehouse-reporting.service';

@Component({
    selector: 'app-report-adjustments',
    templateUrl: './report-adjustments.component.html',
    styleUrls: ['./report-adjustments.component.scss'],
    standalone: false
})
export class ReportAdjustmentsComponent implements OnInit {

  searchFilters: WarehousingReportFilters = new WarehousingReportFilters();

  warehouses: Warehouse[] = [];
  locations: WarehouseLocation[] = [];
  products: Product[] = [];
  references: ProductStockMovementReference[] = [];

  adjustments: MovementRow[] = [];

  totals: AdjustmentTotals = new AdjustmentTotals();

  /** Server-side breakdown by reference code — where the write-offs were booked. */
  byReference: AdjustmentByReference[] = [];

  hasRun: boolean = false;
  running: boolean = false;

  constructor(private warehouseReportingService: WarehouseReportingService,
    private warehouseService: WarehouseService,
    private warehouseLocationService: WarehouseLocationService,
    private stockMovementReferenceService: StockMovementReferenceService,
    private productService: ProductService,
    private stockQuantityService: StockQuantityService,
    private stockWeightService: StockWeightService,
    private reportCsvService: ReportCsvService) { }

  ngOnInit(): void {
    this.warehouseService.getAllWarehouses()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouses');
      return e;
    }))
    .subscribe((data: Warehouse[]) => {
      this.warehouses = data;
    })

    this.productService.getAllActiveProducts()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load products');
      return e;
    }))
    .subscribe((data: Product[]) => {
      this.products = data;
    })

    this.stockMovementReferenceService.getAllStockMovementReferences()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load movement references');
      return e;
    }))
    .subscribe((data: ProductStockMovementReference[]) => {
      this.references = data;
    })
  }

  /**
   * Locations are scoped by warehouseId and the same aisle/shelf can exist at two sites,
   * so the location list only becomes meaningful once a warehouse is chosen.
   */
  onWarehouseChanged(warehouseId: any) {
    this.searchFilters.warehouseId = +warehouseId;
    this.searchFilters.warehouseLocationId = -1;
    this.locations = [];

    if (this.searchFilters.warehouseId === -1) {
      return;
    }

    this.warehouseLocationService.getWarehouseLocationsForWarehouse(this.searchFilters.warehouseId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouse locations');
      return e;
    }))
    .subscribe((data: WarehouseLocation[]) => {
      this.locations = data;
    })
  }

  get datesInvalid(): boolean {
    if (!this.searchFilters.startDate || !this.searchFilters.endDate) {
      return false;
    }

    return moment(this.searchFilters.endDate).isBefore(moment(this.searchFilters.startDate));
  }

  loadReport() {
    if (this.datesInvalid) {
      return;
    }

    this.running = true;

    this.warehouseReportingService.getAdjustments(this.requestBody())
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      this.running = false;
      alert('could not get report');
      return e;
    }))
    .subscribe((result: AdjustmentsResult) => {
      this.adjustments = result && result.rows ? result.rows : [];
      this.totals = result && result.totals ? result.totals : new AdjustmentTotals();
      this.byReference = result && result.byReference ? result.byReference : [];
      this.hasRun = true;
      this.running = false;
    })
  }

  /**
   * Only the filters actually set. The backend rejects what it cannot parse rather than
   * ignoring it, and dates go up as plain YYYY-MM-DD — it extends a bare end date to the end
   * of that day itself, so widening it here as well would apply the range twice.
   */
  private requestBody(): any {
    const body: any = {};

    if (this.searchFilters.startDate) {
      body.from = this.searchFilters.startDate;
    }

    if (this.searchFilters.endDate) {
      body.to = this.searchFilters.endDate;
    }

    if (this.searchFilters.warehouseId && this.searchFilters.warehouseId !== -1) {
      body.warehouseId = this.searchFilters.warehouseId;
    }

    if (this.searchFilters.productId && +this.searchFilters.productId !== -1) {
      body.productId = +this.searchFilters.productId;
    }

    return body;
  }

  /** The net comes from the backend, which classified these rows in the first place. */
  getNetQty(): string {
    return this.stockQuantityService.round(this.totals.netQty).toString();
  }

  /**
   * True when some of these movements carry no unit cost, so the value figures below only
   * cover part of what moved. Never let a money total stand on its own here.
   */
  get hasUncosted(): boolean {
    return this.totals.uncostedMovements > 0 || this.totals.productsWithoutCostBasis > 0;
  }

  formatMoney(value: number): string {
    if (value === null || value === undefined) {
      return '—';
    }

    return '£' + Number(value).toFixed(2);
  }

  /**
   * True when some of these movements were not weighed at both ends, so `totals.netWeight`
   * below only covers part of what moved. Mirrors `hasUncosted` for the same reason: never let
   * a weight total stand on its own.
   */
  get hasUnweighed(): boolean {
    return this.totals.unweighedMovements > 0;
  }

  /**
   * A weight for display, whole-set total or single row alike: blank where nothing was weighed,
   * and a real `0` where a tared scale genuinely read nothing. `StockWeightService` owns that
   * rule; never `Number()` and never `|| 0` here, because `Number(null)` is 0 and would present
   * an unweighed movement as one that weighed nothing.
   */
  formatWeight(weight: number): string {
    return this.stockWeightService.format(weight);
  }

  isNegative(qty: number): boolean {
    return this.stockQuantityService.isNegative(qty);
  }

  formatQty(qty: number): string {
    return this.stockQuantityService.round(qty).toString();
  }

  formatDateTime(value: string): string {
    return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '';
  }

  exportToCSV() {
    // This report backs insurance claims and customer write-off queries, so an unweighed
    // movement must export as an empty cell and never as 0 — a spreadsheet sums a column of
    // blanks without complaint, but sums a column of zeroes just as readily, and a zero here
    // would be quoted as a real weight on stock nobody put on a scale. `formatWeight` already
    // draws that line; this screen just has to use it rather than a bare value.
    const headers: string[] = ['Id', 'Date', 'Type', 'Product', 'SKU', 'Warehouse', 'Location',
      'Signed Qty', 'Start Weight', 'End Weight', 'Net Weight', 'Reference', 'Reference Code', 'Created By'];

    const rows: any[][] = this.adjustments.map((adjustment: MovementRow) => [
      adjustment.id,
      this.formatDateTime(adjustment.createdAt),
      adjustment.movementType,
      adjustment.productName,
      adjustment.sku,
      adjustment.warehouseName,
      adjustment.locationLabel,
      this.formatQty(adjustment.signedQty),
      this.formatWeight(adjustment.startWeight),
      this.formatWeight(adjustment.endWeight),
      this.formatWeight(adjustment.netWeight),
      adjustment.referenceName,
      adjustment.referenceCode,
      adjustment.createdBy
    ]);

    this.reportCsvService.download('stock-adjustments', headers, rows);
  }
}
